import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useLang } from "@/i18n";
import { useCart, MAX_QTY } from "@/context/CartContext";
import { formatPrice } from "@/lib/format";
import { normalizePostal, isPostalValid, isZoneBlocked } from "@/lib/shipping";
import { BagPack } from "@/components/BagPack";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartDrawer = () => {
  const { t, lang } = useLang();
  const {
    items,
    isOpen,
    setIsOpen,
    updateQty,
    removeItem,
    subtotal,
    total,
    shipping,
    shippingCost,
    missingForFree,
    isItemUnavailable,
    hasUnavailable,
    removeUnavailable,
  } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [postal, setPostal] = useState(() => localStorage.getItem("naguara_postal") || "");
  const panelRef = useRef(null);

  const blockedPrefixes = shipping.blocked_prefixes || [];
  const zoneBlocked = isZoneBlocked(postal, blockedPrefixes);
  const postalOk = !shipping.postal_required || (isPostalValid(postal) && !zoneBlocked);
  const freeFrom = shipping.configured ? shipping.free_from : null;
  const progress = freeFrom ? Math.min(100, Math.round((subtotal / freeFrom) * 100)) : 0;

  // Escape closes the drawer and the page behind it never scrolls.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.querySelector("button")?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [isOpen, setIsOpen]);

  useEffect(() => {
    if (!isOpen) setError("");
  }, [isOpen]);

  useEffect(() => {
    localStorage.setItem("naguara_postal", postal);
  }, [postal]);

  const checkout = async () => {
    if (items.length === 0 || hasUnavailable || loading) return;
    if (!postalOk) {
      setError(zoneBlocked ? t.cart.zoneBlocked : t.cart.postalInvalid);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        postal_code: postal,
      });
      if (!res.data?.url) throw new Error("No checkout URL");
      window.location.href = res.data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.error : detail;
      if (code === "zone_unavailable") setError(t.cart.zoneBlocked);
      else if (code === "postal_required") setError(t.cart.postalInvalid);
      else if (e?.response?.status === 409) setError(t.avail.cartBlocked);
      else setError(t.cart.error);
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-[#121212]/60 z-[60]"
          />
          <motion.aside
            ref={panelRef}
            data-testid="cart-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={t.cart.title}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-[#F5F1E8] z-[70] flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between px-7 py-5 border-b border-[#3B2523]/10">
              <h2 className="font-display text-2xl font-bold text-[#3B2523]">{t.cart.title}</h2>
              <button
                data-testid="cart-close-btn"
                onClick={() => setIsOpen(false)}
                aria-label={t.cart.close}
                className="w-11 h-11 -mr-2 flex items-center justify-center rounded-full text-[#3B2523] transition-colors hover:bg-[#3B2523]/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B2523]"
              >
                <X size={24} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 text-[#3B2523]/70">
                <ShoppingBag size={48} strokeWidth={1} />
                <p>{t.cart.empty}</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto px-7 py-6 flex flex-col gap-6">
                {items.map((i) => (
                  <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-4">
                    <div className="w-16 shrink-0 rounded-lg bg-[#F1ECE0] p-1.5">
                      <BagPack product={i} mini className="w-full" testId={`cart-pack-${i.product_id}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between gap-3">
                        <h3 className="font-display font-bold text-[#3B2523]">{i.name}</h3>
                        <button
                          data-testid={`cart-remove-${i.product_id}`}
                          onClick={() => removeItem(i.product_id)}
                          aria-label={`${t.cart.remove} ${i.name}`}
                          className="w-11 h-11 -mt-2 -mr-2 shrink-0 flex items-center justify-center rounded-full text-[#3B2523]/70 transition-colors hover:text-[#3B2523] hover:bg-[#3B2523]/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B2523]"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <p className="text-[#3B2523]/70 text-sm mt-1">
                        {formatPrice(i.price, lang)}
                        {i.quantity > 1 && (
                          <span data-testid={`cart-line-total-${i.product_id}`} className="text-[#3B2523] font-semibold">
                            {` · ${formatPrice(i.price * i.quantity, lang)}`}
                          </span>
                        )}
                      </p>
                      {isItemUnavailable(i) && (
                        <p
                          data-testid={`cart-unavailable-${i.product_id}`}
                          className="mt-1.5 inline-block bg-[#8C2F1E] text-[#FFF6EC] text-[11px] font-bold uppercase tracking-[0.1em] px-2.5 py-1 rounded-full"
                        >
                          {t.avail.cartItem}
                        </p>
                      )}
                      <div className="flex items-center gap-1.5 mt-2">
                        <button
                          data-testid={`cart-dec-${i.product_id}`}
                          onClick={() => updateQty(i.product_id, -1)}
                          aria-label={t.cart.less}
                          className="w-11 h-11 flex items-center justify-center rounded-full text-[#3B2523] transition-colors hover:bg-[#3B2523]/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B2523]"
                        >
                          <Minus size={15} />
                        </button>
                        <span
                          data-testid={`cart-qty-${i.product_id}`}
                          aria-live="polite"
                          className="font-semibold text-[#3B2523] w-6 text-center"
                        >
                          {i.quantity}
                        </span>
                        <button
                          data-testid={`cart-inc-${i.product_id}`}
                          onClick={() => updateQty(i.product_id, 1)}
                          disabled={i.quantity >= MAX_QTY}
                          aria-label={t.cart.more}
                          title={i.quantity >= MAX_QTY ? t.cart.maxQty : undefined}
                          className="w-11 h-11 flex items-center justify-center rounded-full text-[#3B2523] transition-colors hover:bg-[#3B2523]/8 disabled:opacity-35 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B2523]"
                        >
                          <Plus size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {items.length > 0 && (
              <div className="px-7 py-6 border-t border-[#3B2523]/10">
                <div className="flex justify-between items-center text-sm text-[#3B2523]/70">
                  <span>{t.cart.subtotal}</span>
                  <span data-testid="cart-subtotal">{formatPrice(subtotal, lang)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-[#3B2523]/70 mt-2">
                  <span>{t.cart.shipping}</span>
                  <span data-testid="cart-shipping">
                    {!shipping.configured
                      ? t.cart.shippingIncluded
                      : shippingCost === 0
                        ? t.cart.shippingFree
                        : formatPrice(shippingCost, lang)}
                  </span>
                </div>
                {shipping.configured && freeFrom != null && (
                  <div className="mt-3" data-testid="cart-free-progress">
                    <div className="h-1.5 rounded-full bg-[#3B2523]/12 overflow-hidden">
                      <div
                        data-testid="cart-free-bar"
                        style={{ width: `${missingForFree > 0 ? progress : 100}%` }}
                        className={`h-full rounded-full transition-[width] duration-500 ease-out ${
                          missingForFree > 0 ? "bg-[#C9A227]" : "bg-[#2F6B4F]"
                        }`}
                      />
                    </div>
                    <p
                      data-testid={missingForFree > 0 ? "cart-free-hint" : "cart-free-reached"}
                      aria-live="polite"
                      className={`text-sm mt-2 font-semibold ${
                        missingForFree > 0 ? "text-[#8C6B2F]" : "text-[#2F6B4F]"
                      }`}
                    >
                      {missingForFree > 0
                        ? t.cart.freeMissing.replace("{amount}", formatPrice(missingForFree, lang))
                        : t.cart.freeReached}
                    </p>
                  </div>
                )}

                <div className="flex justify-between items-center mt-4 mb-5 pt-4 border-t border-[#3B2523]/10">
                  <span className="text-[#3B2523] font-semibold">{t.cart.total}</span>
                  <span data-testid="cart-total" className="font-display text-2xl font-bold text-[#3B2523]">
                    {formatPrice(total, lang)}
                  </span>
                </div>

                {hasUnavailable && (
                  <div
                    data-testid="cart-blocked-notice"
                    role="alert"
                    className="mb-4 rounded-2xl border border-[#8C2F1E]/30 bg-[#8C2F1E]/8 px-4 py-3"
                  >
                    <p className="text-[#8C2F1E] text-sm leading-relaxed">{t.avail.cartBlocked}</p>
                    <button
                      type="button"
                      data-testid="cart-remove-unavailable"
                      onClick={removeUnavailable}
                      className="mt-2 min-h-[44px] text-sm font-bold text-[#8C2F1E] underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8C2F1E] rounded"
                    >
                      {t.avail.cartRemove}
                    </button>
                  </div>
                )}

                {shipping.postal_required && (
                  <div className="mb-4">
                    <label htmlFor="cart-postal" className="block text-[#3B2523] text-sm font-semibold">
                      {t.cart.postalLabel}
                    </label>
                    <input
                      id="cart-postal"
                      data-testid="cart-postal-input"
                      value={postal}
                      onChange={(e) => {
                        setPostal(normalizePostal(e.target.value));
                        setError("");
                      }}
                      inputMode="numeric"
                      autoComplete="postal-code"
                      maxLength={5}
                      placeholder={t.cart.postalPlaceholder}
                      aria-invalid={postal.length === 5 && !postalOk}
                      aria-describedby="cart-postal-hint"
                      className="mt-2 w-full min-h-[48px] rounded-2xl border border-[#3B2523]/25 bg-[#FFFCF5] px-4 text-[#3B2523] text-base tracking-[0.14em] placeholder:text-[#3B2523]/35 placeholder:tracking-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B2523]"
                    />
                    <p
                      id="cart-postal-hint"
                      data-testid="cart-postal-hint"
                      className="text-[#3B2523]/70 text-xs mt-2 leading-relaxed"
                    >
                      {t.cart.postalHint}
                    </p>
                    {zoneBlocked && (
                      <p
                        data-testid="cart-zone-blocked"
                        role="alert"
                        className="mt-3 rounded-2xl border border-[#8C2F1E]/30 bg-[#8C2F1E]/8 px-4 py-3 text-[#8C2F1E] text-sm leading-relaxed"
                      >
                        {t.cart.zoneBlocked}
                      </p>
                    )}
                  </div>
                )}

                <button
                  data-testid="checkout-btn"
                  onClick={checkout}
                  disabled={loading || hasUnavailable || !postalOk}
                  aria-disabled={loading || hasUnavailable || !postalOk}
                  className="w-full min-h-[48px] bg-[#3B2523] text-[#F5F1E8] font-semibold py-4 rounded-full transition-all duration-300 hover:bg-[#F4D35E] hover:text-[#3B2523] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#3B2523] disabled:hover:text-[#F5F1E8]"
                >
                  {loading ? t.cart.processing : t.cart.checkout}
                </button>

                <div aria-live="polite" role="status" data-testid="cart-status">
                  {error && <p className="text-[#8C2F1E] text-sm mt-3">{error}</p>}
                </div>

                <p className="text-[#3B2523]/70 text-xs mt-3 text-center leading-relaxed">
                  {t.cart.secure}
                </p>

                {shipping.configured && (
                  <p
                    data-testid="cart-conditions"
                    className="text-[#3B2523]/70 text-xs mt-1.5 text-center leading-relaxed"
                  >
                    {t.cart.conditions
                      .replace("{flat}", formatPrice(shipping.flat, lang))
                      .replace("{free}", freeFrom != null ? formatPrice(freeFrom, lang) : "—")}
                  </p>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
